import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Stethoscope, Home, Cannabis, Sparkles, HeartHandshake,
    Leaf, Dna, Activity, Users, Award, MapPin, Phone, Mail, MessageCircle,
    Calendar, ClipboardCheck, TrendingUp, Quote, ArrowUpRight,
    Brain, ShieldCheck, Smile,
} from "lucide-react";
import { TID } from "@/lib/testIds";
import api from "@/lib/api";
import HomeVisitReel from "@/components/HomeVisitReel";

// Curated Unsplash imagery — inclusive, active-aging, diverse.
const IMG = (id, w = 1200, q = 82) =>
    `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=${q}`;

const HERO_IMAGES = {
    main: IMG("1658314755707-1fbdf7c40145", 1000),      // cuidador com idoso — acolhimento
    fitness: IMG("1758691462743-f9fc9e430d39", 900),    // conexão intergeracional / cuidado profissional
};

const PILLARS = [
    {
        icon: Dna,
        title: "Medicina de Precisão & Personalizada",
        text: "Genômica, biomarcadores e exames avançados para um plano de longevidade construído sob medida — do seu perfil metabólico ao seu ritmo de envelhecimento biológico.",
    },
    {
        icon: Leaf,
        title: "Medicina do Estilo de Vida",
        text: "Nutrição, sono restaurador, movimento, propósito e gestão do estresse — os pilares comprovados para viver mais e melhor.",
    },
    {
        icon: Home,
        title: "Consultas Ambulatoriais & Domiciliares",
        text: "Atendimento no consultório ou em casa, respeitando o momento de vida e a mobilidade da pessoa idosa e de sua família.",
    },
    {
        icon: Activity,
        title: "Doenças Crônicas com Excelência",
        text: "Hipertensão, diabetes, insuficiência cardíaca, DPOC e demências: controle rigoroso, prevenção de crises e qualidade de vida.",
    },
    {
        icon: Brain,
        title: "Avaliação da Cognição e Memória",
        text: "Rastreio detalhado com escalas validadas (MEEM, MoCA, Fluência Verbal) para identificar precocemente alterações e planejar intervenções personalizadas.",
    },
    {
        icon: ShieldCheck,
        title: "Prevenção e Acompanhamento de Demências",
        text: "Estratégias baseadas em evidência para reduzir fatores de risco modificáveis e acompanhar Alzheimer, demência vascular e outras condições neurocognitivas com foco em qualidade de vida.",
    },
    {
        icon: Smile,
        title: "Saúde Mental na Longevidade",
        text: "Cuidado da depressão, ansiedade, insônia e solidão na pessoa idosa — com escuta atenta, abordagem integrativa e articulação com psicoterapia quando necessário.",
    },
    {
        icon: HeartHandshake,
        title: "Saúde Integral do Idoso (AGA)",
        text: "Avaliação Geriátrica Ampla com escalas validadas + plano de cuidado multiprofissional envolvendo paciente, família e cuidadores.",
    },
    {
        icon: Cannabis,
        title: "Cannabis Medicinal",
        text: "Prescrição responsável e acompanhamento personalizado para dor crônica, insônia, ansiedade e transtornos neurológicos.",
    },
];

const STEPS = [
    {
        icon: ClipboardCheck,
        title: "Avaliação inicial",
        text: "Anamnese completa, Avaliação Geriátrica Ampla, exames laboratoriais e biomarcadores de longevidade.",
    },
    {
        icon: Sparkles,
        title: "Plano de longevidade personalizado",
        text: "Estratégia individualizada combinando medicina de precisão, estilo de vida, medicamentos e terapias complementares.",
    },
    {
        icon: TrendingUp,
        title: "Acompanhamento contínuo",
        text: "Equipe multiprofissional, telemedicina, portal do paciente e reavaliações periódicas para ajuste fino ao longo da jornada.",
    },
];

const TRUST_BADGES = [
    { label: "CRM 131939", detail: "Marcos Rodrigo Fernandes" },
    { label: "RQE 80174", detail: "Medicina de Família" },
    { label: "Geriatria", detail: "Cuidado da pessoa idosa" },
    { label: "Medicina de Precisão", detail: "e Personalizada" },
];

export default function LandingPage() {
    const [posts, setPosts] = useState([]);
    const [testimonials, setTestimonials] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [p, t] = await Promise.all([
                    api.get("/blog/posts", { params: { published_only: true } }),
                    api.get("/testimonials"),
                ]);
                setPosts((p.data || []).slice(0, 3));
                setTestimonials((t.data || []).slice(0, 3));
            } catch { /* landing must not crash if content is empty */ }
        })();
    }, []);

    return (
        <div className="min-h-screen bg-base relative overflow-x-hidden" data-testid="landing-page">
            {/* Ambient background blobs */}
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#E8DFCC] opacity-40 blur-3xl pointer-events-none" />
            <div className="absolute top-96 -left-32 w-[400px] h-[400px] rounded-full bg-[#DAE3D5] opacity-40 blur-3xl pointer-events-none" />
            <div className="absolute top-[1400px] right-0 w-[350px] h-[350px] rounded-full bg-[#EFE3D5] opacity-30 blur-3xl pointer-events-none" />

            <Header />

            <Hero />

            <HomeVisitReel />

            <TrustBar />

            <Pillars />

            <HowItWorks />

            <AboutDoctor />

            <BlogPreview posts={posts} />

            <TestimonialsPreview testimonials={testimonials} />

            <FooterCTA />
        </div>
    );
}

// ---------- Header ----------
function Header() {
    return (
        <header className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3" data-testid="landing-brand">
                <div className="w-11 h-11 rounded-xl bg-brand text-white flex items-center justify-center shadow-sm">
                    <Leaf size={22} />
                </div>
                <div className="leading-tight">
                    <div className="font-display text-base sm:text-lg font-semibold text-brand">Clínica da Longevidade</div>
                    <div className="text-[11px] sm:text-xs text-muted-clinical">Cuidado integral, humanizado e acolhedor</div>
                </div>
            </Link>
            <nav className="flex items-center gap-1 sm:gap-2">
                <Link to="/blog" data-testid="landing-blog-link" className="btn-ghost text-sm !hidden md:!inline-flex">Minuto da Saúde</Link>
                <Link to="/depoimentos" data-testid="landing-testimonials-link" className="btn-ghost text-sm !hidden lg:!inline-flex">Depoimentos</Link>
                <Link to="/login" data-testid={TID.landing.loginBtn} className="btn-ghost text-sm">Entrar</Link>
                <Link to="/register" data-testid={TID.landing.registerBtn} className="btn-primary text-sm">Criar conta</Link>
            </nav>
        </header>
    );
}

// ---------- Hero ----------
function Hero() {
    return (
        <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-6 pb-20" data-testid="landing-hero">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-alt text-brand text-[11px] font-semibold uppercase tracking-widest mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-accent)] animate-pulse" />
                        Clínica da Longevidade — Dr. Marcos Fernandes
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-brand leading-[1.05] tracking-tight">
                        Viver mais,
                        <br />
                        com <span className="text-[var(--brand-accent)]">saúde, propósito</span>
                        <br />
                        e autonomia.
                    </h1>
                    <p className="mt-6 text-base sm:text-lg text-[var(--text-secondary)] max-w-xl leading-relaxed">
                        Cuidado da pessoa idosa e de sua família com medicina de precisão, personalizada e humanizada — do envelhecimento saudável ao cuidado geriátrico completo.
                    </p>
                    <p className="mt-4 text-sm sm:text-base text-brand-secondary max-w-xl leading-relaxed">
                        <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--brand-accent)] mr-2">Nossa missão</span>
                        Construir com você uma sólida <span className="text-brand font-medium">reserva cognitiva e funcional</span> — para envelhecer com saúde, autonomia e vitalidade.
                    </p>

                    <div className="mt-6 flex flex-wrap gap-2">
                        {["Precisão", "Estilo de Vida", "Integral", "Humano"].map((tag) => (
                            <span key={tag} className="text-xs px-3 py-1 rounded-full bg-white border border-[var(--border-organic)] text-brand-secondary font-medium">
                                {tag}
                            </span>
                        ))}
                    </div>

                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/register" data-testid={TID.landing.heroCta} className="btn-primary group">
                            Agendar avaliação <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                        <Link to="/login" className="btn-secondary">Já sou paciente</Link>
                    </div>
                </div>

                {/* Right — image collage */}
                <div className="relative">
                    <div className="grid grid-cols-6 gap-3">
                        <div className="col-span-4 row-span-2 rounded-3xl overflow-hidden aspect-[4/5] shadow-lg">
                            <img src={HERO_IMAGES.main} alt="Cuidado humanizado da pessoa idosa" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="col-span-2 rounded-3xl bg-alt border border-[var(--border-organic)] p-4 flex flex-col justify-between aspect-square shadow-sm">
                            <Users size={22} className="text-brand" />
                            <div>
                                <div className="font-display text-xl leading-tight text-brand font-semibold">Cuidado integral</div>
                                <div className="text-[11px] text-muted-clinical mt-1">Paciente, família e cuidadores</div>
                            </div>
                        </div>
                        <div className="col-span-2 rounded-3xl bg-brand text-white p-5 flex flex-col justify-between aspect-square shadow-lg">
                            <Dna size={26} className="opacity-90" />
                            <div>
                                <div className="font-display text-lg leading-tight">Longevidade</div>
                                <div className="text-[11px] opacity-80 mt-1">Medicina de precisão</div>
                            </div>
                        </div>
                        <div className="col-span-6 rounded-3xl overflow-hidden aspect-[3/1] shadow-lg">
                            <img src={HERO_IMAGES.fitness} alt="Cuidado clínico e conexão" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------- Trust bar ----------
function TrustBar() {
    return (
        <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="landing-trust">
            <div className="rounded-2xl bg-white/70 backdrop-blur border border-[var(--border-organic)] px-6 py-5 flex flex-wrap items-center justify-around gap-4">
                {TRUST_BADGES.map((b) => (
                    <div key={b.label} className="flex items-center gap-3 min-w-0">
                        <Award size={18} className="text-brand shrink-0" />
                        <div className="leading-tight">
                            <div className="font-display text-sm font-semibold text-brand">{b.label}</div>
                            <div className="text-[11px] text-muted-clinical">{b.detail}</div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// ---------- Pillars ----------
function Pillars() {
    return (
        <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="landing-pillars">
            <div className="mb-10 max-w-2xl">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-secondary mb-2">Nossos pilares</div>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold text-brand leading-tight">
                    O que compõe o cuidado <span className="text-[var(--brand-accent)]">integral</span> de longevidade
                </h2>
                <p className="mt-3 text-base text-[var(--text-secondary)]">
                    Uma abordagem completa que une ciência de ponta, escuta clínica e o acolhimento que a pessoa idosa e sua família merecem.
                </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {PILLARS.map((p, i) => (
                    <div
                        key={p.title}
                        className="group card-earthy bg-white hover:shadow-md transition-shadow cursor-default"
                        data-testid={`landing-pillar-${i}`}
                    >
                        <div className="w-11 h-11 rounded-xl bg-alt flex items-center justify-center mb-4 group-hover:bg-brand group-hover:text-white transition-colors">
                            <p.icon size={22} />
                        </div>
                        <h3 className="font-display text-lg font-semibold text-brand mb-2">{p.title}</h3>
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{p.text}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

// ---------- How it works ----------
function HowItWorks() {
    return (
        <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="landing-how">
            <div className="mb-10 max-w-2xl">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-secondary mb-2">Como funciona</div>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold text-brand leading-tight">
                    Sua jornada de longevidade em <span className="text-[var(--brand-accent)]">3 passos</span>
                </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4 relative">
                {STEPS.map((s, i) => (
                    <div key={s.title} className="card-earthy bg-white relative" data-testid={`landing-step-${i}`}>
                        <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center font-display font-semibold shadow-md">
                            {i + 1}
                        </div>
                        <s.icon size={26} className="text-[var(--brand-accent)] mb-4" />
                        <h3 className="font-display text-lg font-semibold text-brand mb-2">{s.title}</h3>
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{s.text}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

// ---------- About Doctor ----------
function AboutDoctor() {
    return (
        <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="landing-about">
            <div className="grid lg:grid-cols-5 gap-8 items-center">
                <div className="lg:col-span-2">
                    <div className="rounded-3xl overflow-hidden shadow-lg aspect-[4/5] bg-gradient-to-br from-alt to-[#e8e0d0] relative">
                        <img
                            src="/dr-marcos.jpg"
                            alt="Dr. Marcos Rodrigo Fernandes"
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                            data-testid="landing-doctor-photo"
                        />
                    </div>
                </div>
                <div className="lg:col-span-3">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-secondary mb-2">Sobre o médico</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-semibold text-brand leading-tight mb-4">
                        Dr. Marcos Rodrigo Fernandes
                    </h2>
                    <div className="flex flex-wrap gap-2 mb-5">
                        <span className="text-xs px-3 py-1 rounded-full bg-brand text-white font-medium">CRM 131939</span>
                        <span className="text-xs px-3 py-1 rounded-full bg-brand text-white font-medium">RQE 80174</span>
                        <span className="text-xs px-3 py-1 rounded-full bg-white border border-[var(--border-organic)] text-brand-secondary font-medium">Medicina de Família</span>
                        <span className="text-xs px-3 py-1 rounded-full bg-white border border-[var(--border-organic)] text-brand-secondary font-medium">Geriatria</span>
                        <span className="text-xs px-3 py-1 rounded-full bg-white border border-[var(--border-organic)] text-brand-secondary font-medium">Cuidados Integrativos</span>
                        <span className="text-xs px-3 py-1 rounded-full bg-white border border-[var(--border-organic)] text-brand-secondary font-medium">Medicina de Precisão</span>
                        <span className="text-xs px-3 py-1 rounded-full bg-white border border-[var(--border-organic)] text-brand-secondary font-medium">Medicina do Estilo de Vida</span>
                        <span className="text-xs px-3 py-1 rounded-full bg-white border border-[var(--border-organic)] text-brand-secondary font-medium">Cannabis Medicinal</span>
                        <span className="text-xs px-3 py-1 rounded-full bg-white border border-[var(--border-organic)] text-brand-secondary font-medium">Saúde Mental</span>
                        <span className="text-xs px-3 py-1 rounded-full bg-white border border-[var(--border-organic)] text-brand-secondary font-medium">Cognição e Memória</span>
                    </div>
                    <p className="text-base text-[var(--text-secondary)] leading-relaxed mb-4">
                        Médico especialista em <strong className="text-brand">Medicina da Família (RQE 80174)</strong>, com pós-graduação em <strong className="text-brand">Geriatria</strong>, <strong className="text-brand">Cuidados Integrativos</strong> e <strong className="text-brand">Medicina de Precisão e Personalizada</strong>. Dedica-se ao cuidado integral da pessoa idosa e ao envelhecimento saudável, unindo a rigorosa Avaliação Geriátrica Ampla à medicina de precisão e ao estilo de vida como pilares centrais da longevidade.
                    </p>
                    <p className="text-base text-[var(--text-secondary)] leading-relaxed mb-6">
                        Acredita que envelhecer bem é fruto de <strong className="text-brand">escolhas informadas, acompanhamento contínuo e escuta atenta</strong> — sempre respeitando a história, os valores e o ritmo de cada família.
                    </p>
                    <Link to="/register" className="btn-primary" data-testid="landing-about-cta">
                        Agendar avaliação <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        </section>
    );
}

// ---------- Blog preview ----------
function BlogPreview({ posts }) {
    if (!posts || posts.length === 0) return null;
    return (
        <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="landing-blog-preview">
            <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-secondary mb-2">Minuto da Saúde</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-semibold text-brand leading-tight">
                        Conteúdo para viver <span className="text-[var(--brand-accent)]">mais e melhor</span>
                    </h2>
                </div>
                <Link to="/blog" className="text-sm text-brand font-medium hover:underline flex items-center gap-1" data-testid="landing-blog-all-link">
                    Ver todos os artigos <ArrowUpRight size={14} />
                </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
                {posts.map((p) => (
                    <Link
                        key={p.id}
                        to={`/blog/${p.slug}`}
                        className="group card-earthy bg-white hover:shadow-md transition-shadow overflow-hidden p-0"
                        data-testid={`landing-post-${p.slug}`}
                    >
                        {p.cover_image && (
                            <div className="aspect-[16/10] overflow-hidden">
                                <img
                                    src={p.cover_image.startsWith("http") ? p.cover_image : `${api.defaults.baseURL}/media/${p.cover_image}`}
                                    alt={p.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    loading="lazy"
                                />
                            </div>
                        )}
                        <div className="p-5">
                            {p.category && (
                                <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-secondary mb-2">
                                    {p.category.replace(/_/g, " ")}
                                </div>
                            )}
                            <h3 className="font-display text-lg font-semibold text-brand mb-2 line-clamp-2 group-hover:text-[var(--brand-accent)] transition-colors">
                                {p.title}
                            </h3>
                            {p.excerpt && <p className="text-sm text-[var(--text-secondary)] line-clamp-3">{p.excerpt}</p>}
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
}

// ---------- Testimonials ----------
function TestimonialsPreview({ testimonials }) {
    if (!testimonials || testimonials.length === 0) return null;
    return (
        <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="landing-testimonials">
            <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-secondary mb-2">Depoimentos</div>
                    <h2 className="font-display text-3xl sm:text-4xl font-semibold text-brand leading-tight">
                        Histórias de <span className="text-[var(--brand-accent)]">quem cuida</span>
                    </h2>
                </div>
                <Link to="/depoimentos" className="text-sm text-brand font-medium hover:underline flex items-center gap-1" data-testid="landing-testimonials-all-link">
                    Ver todos <ArrowUpRight size={14} />
                </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
                {testimonials.map((t) => (
                    <div key={t.id} className="card-earthy bg-white" data-testid={`landing-testimonial-${t.id}`}>
                        <Quote size={22} className="text-[var(--brand-accent)] mb-3 opacity-70" />
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4 line-clamp-6">
                            {t.content || t.text || t.testimonial}
                        </p>
                        <div className="flex items-center gap-3 pt-3 border-t border-[var(--border-organic)]">
                            <div className="w-10 h-10 rounded-full bg-alt flex items-center justify-center font-semibold text-brand">
                                {(t.author_name || t.name || "?").charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <div className="font-display text-sm font-semibold text-brand">
                                    {t.author_name || t.name}
                                </div>
                                {t.relation && <div className="text-[11px] text-muted-clinical">{t.relation}</div>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// ---------- Footer CTA ----------
function FooterCTA() {
    return (
        <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20" data-testid="landing-footer-cta">
            <div className="rounded-3xl bg-brand text-white p-8 sm:p-12 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/5 blur-2xl" />
                <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white/5 blur-2xl" />
                <div className="relative grid lg:grid-cols-5 gap-8 items-center">
                    <div className="lg:col-span-3">
                        <div className="text-[11px] font-semibold uppercase tracking-widest opacity-80 mb-3">Pronto para começar?</div>
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-4">
                            Sua jornada de longevidade começa hoje.
                        </h2>
                        <p className="text-base opacity-90 max-w-xl leading-relaxed">
                            Agende uma avaliação inicial e descubra um plano personalizado para viver mais anos, com mais saúde, autonomia e sentido.
                        </p>
                    </div>
                    <div className="lg:col-span-2 space-y-3">
                        <Link
                            to="/register"
                            data-testid="landing-footer-cta-btn"
                            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-white text-brand font-semibold hover:bg-[var(--brand-accent)] hover:text-white transition-colors"
                        >
                            <Calendar size={18} /> Agendar avaliação
                        </Link>
                        <Link
                            to="/login"
                            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-white/40 text-white font-medium hover:bg-white/10 transition-colors"
                        >
                            <ArrowRight size={16} /> Já sou paciente
                        </Link>
                    </div>
                </div>
            </div>

            {/* Contact / bottom bar */}
            <div className="mt-10 grid md:grid-cols-3 gap-6 text-sm">
                <div>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand text-white flex items-center justify-center">
                            <Leaf size={20} />
                        </div>
                        <div className="leading-tight">
                            <div className="font-display font-semibold text-brand">Clínica da Longevidade</div>
                            <div className="text-[11px] text-muted-clinical">Cuidado integral, humanizado e acolhedor</div>
                        </div>
                    </div>
                    <p className="mt-4 text-xs text-muted-clinical leading-relaxed">
                        Dr. Marcos Rodrigo Fernandes — CRM 131939 · RQE 80174 (Medicina de Família)<br/>
                        Geriatria · Cuidados Integrativos · Medicina de Precisão · Cannabis Medicinal
                    </p>
                </div>
                <div>
                    <div className="font-display font-semibold text-brand mb-3">Contato</div>
                    <ul className="space-y-2 text-brand-secondary">
                        <li className="flex items-center gap-2"><Phone size={14} /> <span>Em breve</span></li>
                        <li className="flex items-center gap-2"><MessageCircle size={14} /> <span>WhatsApp em breve</span></li>
                        <li className="flex items-center gap-2"><Mail size={14} /> <span>contato@clinicamedicafernandes.com.br</span></li>
                        <li className="flex items-center gap-2"><MapPin size={14} /> <span>Atendimento presencial e telemedicina</span></li>
                    </ul>
                </div>
                <div>
                    <div className="font-display font-semibold text-brand mb-3">Explorar</div>
                    <ul className="space-y-2 text-brand-secondary">
                        <li><Link to="/blog" className="hover:text-brand hover:underline">Minuto da Saúde</Link></li>
                        <li><Link to="/depoimentos" className="hover:text-brand hover:underline">Depoimentos</Link></li>
                        <li><Link to="/login" className="hover:text-brand hover:underline">Entrar no portal</Link></li>
                        <li><Link to="/register" className="hover:text-brand hover:underline">Criar conta</Link></li>
                    </ul>
                </div>
            </div>

            <div className="mt-10 pt-6 border-t border-[var(--border-organic)] flex flex-wrap items-center justify-between gap-3 text-xs text-muted-clinical">
                <div>© {new Date().getFullYear()} Clínica da Longevidade — Todos os direitos reservados</div>
                <div>Conformidade LGPD · Privacidade e segurança dos seus dados</div>
            </div>
        </section>
    );
}
